import type { PolicyFramework } from '@repo/api-codegen';
import type { SecodaThemeShades } from '@repo/theme/types';
import type { SelectablePropertyItem } from '../SingleSelector/types';

export interface ComplianceFrameworkItem extends SelectablePropertyItem {
	label: PolicyFramework;
	value: PolicyFramework;
	color: SecodaThemeShades;
}

export const SELECTABLE_PROPERTY_OPTIONS: Record<
	string,
	SelectablePropertyItem[]
> & {
	complianceFramework: ComplianceFrameworkItem[];
} = {
	published: [
		{
			label: 'Published',
			value: true,
			iconName: 'circleCheckFilled',
			color: 'icon/success/default',
		},
		{
			label: 'Draft',
			value: false,
			iconName: 'circle',
			color: 'icon/secondary/default',
		},
	],
	pii: [
		{
			label: 'PII',
			value: true,
			iconName: 'shieldCheckFilled',
			color: 'icon/primary/default',
		},
		{
			label: 'Not PII',
			value: false,
			textColor: 'text/secondary/default',
			iconName: 'shield',
			color: 'icon/secondary/default',
			shouldHideIcon: true,
		},
	],
	verified: [
		{
			label: 'Verified',
			value: true,
			iconName: 'verified',
			color: 'fill/emphasis/default',
		},
		{
			label: 'Not verified',
			value: false,
			textColor: 'text/secondary/default',
			color: 'icon/secondary/default',
			iconName: 'verifiedUnfilled',
			shouldHideIcon: true,
		},
	],
	priority: [
		{
			label: 'None',
			value: 'NONE',
			iconName: 'priorityNone',
			color: 'icon/secondary/default',
		},
		{
			label: 'Low',
			value: 'LOW',
			iconName: 'priorityLow',
			color: 'icon/secondary/default',
		},
		{
			label: 'Medium',
			value: 'MEDIUM',
			iconName: 'priorityMedium',
			color: 'icon/secondary/default',
		},
		{
			label: 'High',
			value: 'HIGH',
			iconName: 'priorityHigh',
			color: 'icon/secondary/default',
		},
	],
	severity: [
		{
			label: 'None',
			value: 'NONE',
			iconName: 'priorityNone',
		},
		{
			label: 'Low',
			value: 'LOW',
			iconName: 'priorityLow',
		},
		{
			label: 'Medium',
			value: 'MEDIUM',
			iconName: 'priorityMedium',
		},
		{
			label: 'High',
			value: 'HIGH',
			iconName: 'priorityHigh',
		},
	],
	status: [
		{
			label: 'Unanswered',
			value: 'UNANSWERED',
			iconName: 'circle',
			color: 'icon/primary/default',
		},
		{
			label: 'In progress',
			value: 'IN_PROGRESS',
			iconName: 'progress50',
			color: 'icon/caution/default',
		},
		{
			label: 'In review',
			value: 'IN_REVIEW',
			hidden: true,
			iconName: 'progress75',
			color: 'icon/info/default',
		},
		{
			label: 'Answered',
			value: 'ANSWERED',
			iconName: 'circleCheckFilled',
			color: 'icon/success/default',
		},
		{
			label: 'Cancelled',
			value: 'CANCELLED',
			iconName: 'circleXFilled',
			color: 'icon/primary/disabled',
		},
	],
	automationStatus: [
		{
			color: 'fill/success-secondary/default',
			textColor: 'text/success/default',
			label: 'On',
			value: 'enabled',
		},
		{
			color: 'fill/transparent-secondary/default',
			textColor: 'text/primary/default',
			label: 'Off',
			value: 'disabled',
		},
	],
	complianceFramework: [
		{
			label: 'CCPA',
			value: 'CCPA',
			color: 'icon/decorative/grape',
		},
		{
			label: 'CIS',
			value: 'CIS',
			color: 'icon/decorative/sunset',
		},
		{
			label: 'PCI DSS',
			value: 'PCI DSS',
			color: 'icon/decorative/cyan',
		},
		{
			label: 'HIPAA',
			value: 'HIPAA',
			color: 'icon/decorative/lime',
		},
		{
			label: 'GDPR',
			value: 'GDPR',
			color: 'icon/decorative/teal',
		},
		{
			label: 'ISO27001',
			value: 'ISO27001',
			color: 'icon/decorative/orange',
		},
		{
			label: 'NIST',
			value: 'NIST',
			color: 'icon/decorative/indigo',
		},
		{
			label: 'GLBA',
			value: 'GLBA',
			color: 'icon/on-fill-decorative/teal',
		},
		{
			label: 'HITECH',
			value: 'HITECH',
			color: 'icon/decorative/pink',
		},
		{
			label: 'LGPD',
			value: 'LGPD',
			color: 'icon/on-fill-decorative/grape',
		},
		{
			label: 'PIPEDA',
			value: 'PIPEDA',
			color: 'icon/on-fill-decorative/indigo',
		},
		{
			label: 'HITRUST',
			value: 'HITRUST',
			color: 'icon/on-fill-decorative/cyan',
		},
		{
			label: 'SOC2',
			value: 'SOC2',
			color: 'icon/on-fill-decorative/lime',
		},
		{
			label: 'PHIPA',
			value: 'PHIPA',
			color: 'fill/decorative/indigo',
		},
	],
};
